import * as React from 'react';
import {
  getIntrinsicElementProps,
  mergeCallbacks,
  slot,
  useArrowNavigationGroup,
  useMergedRefs,
} from '@fluentui/react-components';
import { useOverflowMaxVisibleItems } from '@fluentui-copilot/react-utilities';
import { useRootStyles } from '../Reference/useReferenceStyles.styles';
import type { ReferenceListProps, ReferenceListState } from './ReferenceList.types';

/**
 * Create the state required to render ReferenceList.
 *
 * The returned state can be modified with hooks such as useReferenceListStyles_unstable,
 * before being passed to renderReferenceList_unstable.
 *
 * @param props - props from this instance of ReferenceList
 * @param ref - reference to root HTMLElement of ReferenceList
 */
export const useReferenceList_unstable = (
  props: ReferenceListProps,
  ref: React.Ref<HTMLDivElement>,
): ReferenceListState => {
  const {
    arrowableRegion,
    children,
    maxVisibleReferences,
    minVisibleReferences,
    showLessButton,
    showMoreButton,
    ...rest
  } = props;

  const [areReferencesExpanded, setAreReferencesExpanded] = React.useState(false);
  const referenceListRef = React.useRef<HTMLDivElement>(null);

  const totalReferencesCount = React.Children.count(children);
  const styles = useRootStyles();
  const resolvedChildren = useOverflowMaxVisibleItems({
    additionalCondition: !areReferencesExpanded,
    children,
    maxVisibleItems: maxVisibleReferences,
    minVisibleItems: minVisibleReferences,
    overflowClassName: styles.overflow,
  });

  // We keep track of whether we are interacting with the overflow button via keyboard or not.
  const overflowButtonTriggeredViaKeyboard = React.useRef(false);

  const focusAttributes = useArrowNavigationGroup({
    axis: 'both',
    circular: true,
    memorizeCurrent: true,
  });

  const onShowMoreButtonClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    setAreReferencesExpanded(true);
  }, []);

  const onShowLessButtonClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    setAreReferencesExpanded(false);
  }, []);

  const shouldUseOverflow =
    (minVisibleReferences === undefined || minVisibleReferences < totalReferencesCount) &&
    maxVisibleReferences !== undefined &&
    !areReferencesExpanded;

  // Since the overflow button is an OverflowItem that's always visible, we need to account for it.
  const minVisibleOverflowItems = minVisibleReferences ? minVisibleReferences + 1 : 1;

  const state: ReferenceListState = {
    areReferencesExpanded,
    overflowButtonTriggeredViaKeyboard,
    maxVisibleReferences,
    minVisibleReferences: minVisibleOverflowItems,
    referenceListRef,
    shouldUseOverflow,
    totalReferencesCount,

    components: {
      root: 'div',
      arrowableRegion: 'div',
      showMoreButton: 'span',
      showLessButton: 'span',
    },
    root: slot.always(
      getIntrinsicElementProps('div', {
        ref: useMergedRefs(ref, referenceListRef),
        ...rest,
      }),
      { elementType: 'div' },
    ),
    arrowableRegion: slot.always(arrowableRegion, {
      defaultProps: { ...focusAttributes, children: resolvedChildren },
      elementType: 'div',
    }),
    showMoreButton: shouldUseOverflow ? slot.optional(showMoreButton, { elementType: 'span' }) : undefined,
    showLessButton: areReferencesExpanded ? slot.optional(showLessButton, { elementType: 'span' }) : undefined,
  };

  if (state.showMoreButton) {
    state.showMoreButton.onClick = mergeCallbacks(onShowMoreButtonClick, state.showMoreButton.onClick);
  }

  if (state.showLessButton) {
    state.showLessButton.onClick = mergeCallbacks(onShowLessButtonClick, state.showLessButton.onClick);
  }

  return state;
};
